import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { FaHashtag } from 'react-icons/fa';
import { Card, Button } from '../components/Layout';
import { Spinner } from '../components/Spinner';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { apiClient } from '../utils/apiClient';
import PageShell from '../components/PageShell';
export const ChannelManagementPage = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    // 有効プロバイダー管理
    const [enabledProviders, setEnabledProviders] = useState([]);
    // チャンネル管理
    const [channels, setChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [showChannelModal, setShowChannelModal] = useState(false);
    const [editingChannel, setEditingChannel] = useState(null);
    const [channelForm, setChannelForm] = useState({
        name: '',
        provider: '',
        type: '',
        config: {},
        description: ''
    });
    const [channelFilter, setChannelFilter] = useState({
        provider: 'all',
        status: 'all',
        search: ''
    });
    // チャンネル削除確認ダイアログ
    const [channelToDelete, setChannelToDelete] = useState(null);
    const [deletingChannel, setDeletingChannel] = useState(false);
    useEffect(() => {
        loadChannelData();
        loadEnabledProviders();
    }, []);
    // 初期化時にチャンネルデータを読み込み
    useEffect(() => {
        if (channels.length === 0) {
            loadChannels();
        }
    }, [channels.length]);
    // 有効プロバイダー取得
    const loadEnabledProviders = async () => {
        try {
            const response = await apiClient.get('/api/v1/providers');
            if (response && typeof response === 'object') {
                const responseData = response;
                // Shibuki API構造: {"providers": [...]}
                if (responseData.providers && Array.isArray(responseData.providers)) {
                    const enabled = responseData.providers
                        .filter((p) => p.enabled)
                        .map((p) => p.name);
                    setEnabledProviders(enabled);
                }
                // apiClient wrapper構造: {success: true, data: {"providers": [...]}}
                else if (responseData.success && responseData.data && responseData.data.providers) {
                    const enabled = responseData.data.providers
                        .filter((p) => p.enabled)
                        .map((p) => p.name);
                    setEnabledProviders(enabled);
                }
                // apiClient wrapper構造: {success: true, data: [...]} (直接配列)
                else if (responseData.success && Array.isArray(responseData.data)) {
                    const enabled = responseData.data
                        .filter((p) => p.enabled)
                        .map((p) => p.name);
                    setEnabledProviders(enabled);
                }
                // その他
                else if (Array.isArray(responseData)) {
                    const enabled = responseData
                        .filter((p) => p.enabled)
                        .map((p) => p.name);
                    setEnabledProviders(enabled);
                }
                else {
                    console.warn('Unexpected providers response structure:', responseData);
                    setEnabledProviders(['websocket', 'slack', 'email', 'google_chat']);
                }
            }
            else {
                console.warn('Invalid providers response:', response);
                setEnabledProviders(['websocket', 'slack', 'email', 'google_chat']);
            }
        }
        catch (err) {
            console.error('プロバイダー取得エラー:', err);
            // フォールバック: デフォルト有効プロバイダー
            setEnabledProviders(['websocket', 'slack', 'email', 'google_chat']);
        }
    };
    const loadChannels = async () => {
        setChannelsLoading(true);
        try {
            const response = await apiClient.get('/api/v1/channels');
            // apiClient wrapper構造: {success: true, data: {channels: [...]}}
            let channelsData = [];
            if (response && typeof response === 'object') {
                const responseData = response;
                if (responseData.success && responseData.data && responseData.data.channels) {
                    channelsData = responseData.data.channels;
                }
                else if (responseData.channels) {
                    channelsData = responseData.channels;
                }
                else if (Array.isArray(responseData)) {
                    channelsData = responseData;
                }
            }
            // 配列であることを確認
            if (Array.isArray(channelsData)) {
                setChannels(channelsData);
            }
            else {
                console.warn('API response is not an array, using empty list');
                setChannels([]);
            }
        }
        catch (err) {
            console.error('チャンネル取得エラー:', err);
            // エラー時は空のリスト
            setChannels([]);
        }
        finally {
            setChannelsLoading(false);
        }
    };
    const loadChannelData = async () => {
        setLoading(true);
        setError(null);
        try {
            // チャンネル管理のみ
            await loadChannels();
        }
        catch (err) {
            console.error('Channel data loading error:', err);
            setError(err.message || 'データの読み込みに失敗しました');
        }
        finally {
            setLoading(false);
        }
    };
    const saveChannelData = async () => {
        // チャンネル管理は保存不要（個別にAPI呼び出し）
        return;
    };
    // チャンネル削除の実行（確認ダイアログ確定時）
    const handleConfirmDeleteChannel = async () => {
        if (!channelToDelete)
            return;
        const channel = channelToDelete;
        try {
            setDeletingChannel(true);
            setError(null);
            await apiClient.delete(`/api/v1/channels/${channel.id}`);
            setSuccess('チャンネルが削除されました');
            // チャンネル一覧を再読み込み
            await loadChannels();
            setChannelToDelete(null);
            // 成功メッセージを3秒後に消す
            setTimeout(() => setSuccess(null), 3000);
        }
        catch (err) {
            console.error('Channel delete error:', err);
            setError(err.message || '削除に失敗しました');
        }
        finally {
            setDeletingChannel(false);
        }
    };
    const renderChannels = () => {
        const safeChannels = Array.isArray(channels) ? channels : [];
        const filteredChannels = safeChannels.filter(channel => {
            if (channelFilter.provider !== 'all' && channel.provider !== channelFilter.provider) {
                return false;
            }
            if (channelFilter.status !== 'all' && channel.status !== channelFilter.status) {
                return false;
            }
            if (channelFilter.search) {
                const searchTerm = channelFilter.search.toLowerCase();
                const name = channel.name?.toLowerCase() || '';
                const description = channel.description?.toLowerCase() || '';
                if (!name.includes(searchTerm) && !description.includes(searchTerm)) {
                    return false;
                }
            }
            return true;
        });
        return (_jsxs("div", { className: "space-y-6", children: [_jsx(Card, { children: _jsx("div", { className: "p-4", children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30D7\u30ED\u30D0\u30A4\u30C0\u30FC" }), _jsxs("select", { value: channelFilter.provider, onChange: (e) => setChannelFilter(prev => ({ ...prev, provider: e.target.value })), className: "w-full px-3 py-2 border border-gray-300 rounded-lg", children: [_jsx("option", { value: "all", children: "\u3059\u3079\u3066" }), Array.isArray(enabledProviders) && enabledProviders.map(provider => (_jsx("option", { value: provider, children: getProviderName(provider) }, provider)))] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30B9\u30C6\u30FC\u30BF\u30B9" }), _jsxs("select", { value: channelFilter.status, onChange: (e) => setChannelFilter(prev => ({ ...prev, status: e.target.value })), className: "w-full px-3 py-2 border border-gray-300 rounded-lg", children: [_jsx("option", { value: "all", children: "\u3059\u3079\u3066" }), _jsx("option", { value: "active", children: "\u30A2\u30AF\u30C6\u30A3\u30D6" }), _jsx("option", { value: "inactive", children: "\u975E\u30A2\u30AF\u30C6\u30A3\u30D6" }), _jsx("option", { value: "error", children: "\u30A8\u30E9\u30FC" })] })] }), _jsxs("div", { className: "md:col-span-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u691C\u7D22" }), _jsx("input", { type: "text", value: channelFilter.search, onChange: (e) => setChannelFilter(prev => ({ ...prev, search: e.target.value })), placeholder: "\u30C1\u30E3\u30F3\u30CD\u30EB\u540D\u307E\u305F\u306F\u8AAC\u660E\u3067\u691C\u7D22...", className: "w-full px-3 py-2 border border-gray-300 rounded-lg" })] })] }) }) }), channelsLoading ? (_jsx("div", { className: "flex justify-center py-8", children: _jsx(Spinner, {}) })) : filteredChannels.length === 0 ? (_jsx(Card, { children: _jsxs("div", { className: "p-8 text-center", children: [_jsx("div", { className: "text-gray-400 text-6xl mb-4", children: "\uD83D\uDCAC" }), _jsx("h3", { className: "text-lg font-medium text-gray-900 mb-2", children: "\u30C1\u30E3\u30F3\u30CD\u30EB\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093" }), _jsx("p", { className: "text-gray-500 mb-4", children: safeChannels.length === 0
                                    ? 'まだチャンネルが作成されていません。チャンネルを追加してください。'
                                    : 'フィルター条件に一致するチャンネルがありません。' }), safeChannels.length === 0 && (_jsxs(Button, { onClick: () => setShowChannelModal(true), variant: "primary", className: "flex items-center mx-auto", children: [_jsx("span", { className: "mr-2", children: "\u2795" }), "\u30C1\u30E3\u30F3\u30CD\u30EB\u3092\u8FFD\u52A0"] }))] }) })) : (_jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6", children: filteredChannels.map(channel => (_jsx(Card, { children: _jsxs("div", { className: "p-6", children: [_jsxs("div", { className: "flex items-start justify-between mb-4", children: [_jsxs("div", { className: "flex items-center", children: [_jsx("span", { className: "text-2xl mr-3", children: getProviderIcon(channel.provider) }), _jsxs("div", { children: [_jsx("h4", { className: "text-lg font-semibold text-gray-900", children: channel.name }), _jsx("p", { className: "text-sm text-gray-500", children: getProviderName(channel.provider) })] })] }), _jsx("span", { className: `px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(channel.status)}`, children: channel.status === 'active' ? 'アクティブ' :
                                                channel.status === 'inactive' ? '非アクティブ' : 'エラー' })] }), channel.description && (_jsx("p", { className: "text-sm text-gray-600 mb-4", children: channel.description })), _jsxs("div", { className: "space-y-2 text-sm text-gray-500", children: [_jsxs("div", { children: ["\u30BF\u30A4\u30D7: ", channel.type] }), channel.provider === 'google_chat' && (channel.config?.space_name || channel.config?.space) && (_jsxs("div", { className: "text-indigo-600 font-medium", children: ["\uD83D\uDCCD Space: ", channel.config?.space_name || channel.config?.space, channel.config?.space_type && ` (${channel.config.space_type})`] })), channel.last_used && (_jsxs("div", { children: ["\u6700\u7D42\u4F7F\u7528: ", new Date(channel.last_used).toLocaleString('ja-JP')] })), _jsxs("div", { children: ["\u4F5C\u6210\u65E5: ", new Date(channel.created_at).toLocaleDateString('ja-JP')] })] }), _jsxs("div", { className: "flex justify-end space-x-2 mt-4", children: [_jsx(Button, { onClick: () => {
                                                setEditingChannel(channel);
                                                setChannelForm(channel);
                                                setShowChannelModal(true);
                                            }, variant: "secondary", size: "sm", children: "\u7DE8\u96C6" }), _jsx(Button, { onClick: async () => {
                                                try {
                                                    setSaving(true);
                                                    setError(null);
                                                    // 特定のチャンネルをテスト
                                                    const testData = {
                                                        platform: channel.provider,
                                                        channel: channel.name,
                                                        message: `テスト通知: ${channel.name}チャンネルへの配信テスト`,
                                                        test_recipient: ''
                                                    };
                                                    await apiClient.post('/api/v1/channels/test', testData);
                                                    setSuccess(`${channel.name}チャンネルへのテスト通知が送信されました`);
                                                    setTimeout(() => setSuccess(null), 3000);
                                                }
                                                catch (err) {
                                                    console.error('Channel test error:', err);
                                                    setError(err.message || 'テスト送信に失敗しました');
                                                }
                                                finally {
                                                    setSaving(false);
                                                }
                                            }, disabled: saving, variant: "secondary", size: "sm", children: saving ? 'テスト中...' : 'テスト' }), _jsx(Button, { onClick: () => setChannelToDelete(channel), disabled: saving, variant: "danger", size: "sm", children: "\u524A\u9664" })] })] }) }, channel.id))) }))] }));
    };
    const renderContent = () => {
        // タブ分岐を削除し、チャンネル管理のみ
        return renderChannels();
    };
    const renderChannelModal = () => {
        if (!showChannelModal)
            return null;
        const getProviderTypes = (provider) => {
            switch (provider) {
                case 'slack':
                    return [
                        { value: 'channel', label: 'チャンネル' },
                        { value: 'dm', label: 'DM' }
                    ];
                case 'google_chat':
                    return [
                        { value: 'space', label: 'スペース' }
                    ];
                case 'email':
                    return [
                        { value: 'distribution_list', label: '配信リスト' },
                        { value: 'single', label: '単一メール' }
                    ];
                case 'websocket':
                    return [
                        { value: 'session', label: 'セッション' },
                        { value: 'room', label: 'ルーム' }
                    ];
                case 'microsoft_teams':
                    return [
                        { value: 'channel', label: 'チャンネル' },
                        { value: 'chat', label: 'チャット' }
                    ];
                case 'discord':
                    return [
                        { value: 'channel', label: 'チャンネル' },
                        { value: 'dm', label: 'DM' }
                    ];
                case 'apns':
                case 'fcm':
                    return [
                        { value: 'device', label: 'デバイス' },
                        { value: 'topic', label: 'トピック' }
                    ];
                default:
                    return [{ value: 'default', label: 'デフォルト' }];
            }
        };
        const getProviderConfigFields = (provider, type) => {
            switch (provider) {
                case 'slack':
                    if (type === 'dm') {
                        return [
                            { key: 'channel', label: 'ユーザーID', type: 'text', placeholder: 'U0123ABCDEF', helpText: '送信先のSlackユーザーIDを入力（例：U0123...）' }
                        ];
                    }
                    return [
                        { key: 'channel', label: 'チャンネル名', type: 'text', placeholder: '#general', helpText: 'Slackチャンネル名を入力（例：#general）' }
                    ];
                case 'google_chat':
                    return [
                        { key: 'webhook_url', label: 'Webhook URL', type: 'url', placeholder: 'https://chat.googleapis.com/v1/spaces/...', helpText: 'Google Chat SpaceのWebhook URLを入力してください' }
                    ];
                case 'email':
                    return [
                        { key: 'emails', label: 'メールアドレス', type: 'text', placeholder: 'admin@teico.com,ops@teico.com', helpText: '複数のメールアドレスをカンマ区切りで入力' }
                    ];
                case 'websocket':
                    return [
                        { key: 'room', label: 'ルーム名', type: 'text', placeholder: 'notification-room', helpText: 'WebSocketルーム名を入力' }
                    ];
                case 'microsoft_teams':
                    return [
                        { key: 'webhook_url', label: 'Webhook URL', type: 'url', placeholder: 'https://your-team.webhook.office.com/webhookb2/...', helpText: 'Microsoft Teams Incoming Webhook URLを入力' }
                    ];
                case 'discord':
                    return [
                        { key: 'channel_id', label: 'チャンネルID', type: 'text', placeholder: '1234567890', helpText: 'Discordチャンネルの数値IDを入力' }
                    ];
                case 'apns':
                case 'fcm':
                    return [
                        { key: 'topic', label: 'トピック', type: 'text', placeholder: 'com.teico.notifications', helpText: 'プッシュ通知のトピック名を入力' }
                    ];
                default:
                    return [];
            }
        };
        return (_jsx(Modal, { isOpen: true, onClose: () => {
                setShowChannelModal(false);
                setEditingChannel(null);
                setChannelForm({ name: '', provider: '', type: '', config: {}, description: '' });
            }, size: "lg", title: editingChannel ? 'チャンネル編集' : 'チャンネル追加', footer: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => {
                            setShowChannelModal(false);
                            setEditingChannel(null);
                            setChannelForm({ name: '', provider: '', type: '', config: {}, description: '' });
                        }, variant: "secondary", children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), _jsx(Button, { onClick: async () => {
                            try {
                                setSaving(true);
                                setError(null);
                                if (editingChannel) {
                                    // 更新処理
                                    await apiClient.put(`/api/v1/channels/${editingChannel.id}`, channelForm);
                                    setSuccess('チャンネルが更新されました');
                                }
                                else {
                                    // 作成処理
                                    await apiClient.post('/api/v1/channels', channelForm);
                                    setSuccess('チャンネルが作成されました');
                                }
                                // チャンネル一覧を再読み込み
                                await loadChannels();
                                // モーダルを閉じる
                                setShowChannelModal(false);
                                setEditingChannel(null);
                                setChannelForm({ name: '', provider: '', type: '', config: {}, description: '' });
                                // 成功メッセージを3秒後に消す
                                setTimeout(() => setSuccess(null), 3000);
                            }
                            catch (err) {
                                console.error('Channel save error:', err);
                                setError(err.message || '保存に失敗しました');
                            }
                            finally {
                                setSaving(false);
                            }
                        }, disabled: saving || !channelForm.name || !channelForm.provider || !channelForm.type, variant: "primary", children: saving ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { size: "sm", className: "mr-2" }), editingChannel ? '更新中...' : '作成中...'] })) : (editingChannel ? '更新' : '作成') })] }), children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30C1\u30E3\u30F3\u30CD\u30EB\u540D" }), _jsx("input", { type: "text", value: channelForm.name || '', onChange: (e) => setChannelForm(prev => ({ ...prev, name: e.target.value })), className: "w-full px-3 py-2 border border-gray-300 rounded-lg", placeholder: "\u30C1\u30E3\u30F3\u30CD\u30EB\u540D\u3092\u5165\u529B" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30D7\u30ED\u30D0\u30A4\u30C0\u30FC" }), _jsxs("select", { value: channelForm.provider || '', onChange: (e) => {
                                    const newProvider = e.target.value;
                                    setChannelForm(prev => ({ ...prev, provider: newProvider, type: '', config: {} }));
                                }, className: "w-full px-3 py-2 border border-gray-300 rounded-lg", children: [_jsx("option", { value: "", children: "\u30D7\u30ED\u30D0\u30A4\u30C0\u30FC\u3092\u9078\u629E" }), Array.isArray(enabledProviders) && enabledProviders.map(provider => (_jsx("option", { value: provider, children: getProviderName(provider) }, provider)))] })] }), channelForm.provider && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u30BF\u30A4\u30D7" }), _jsxs("select", { value: channelForm.type || '', onChange: (e) => setChannelForm(prev => ({ ...prev, type: e.target.value, config: {} })), className: "w-full px-3 py-2 border border-gray-300 rounded-lg", children: [_jsx("option", { value: "", children: "\u30BF\u30A4\u30D7\u3092\u9078\u629E" }), getProviderTypes(channelForm.provider).map(type => (_jsx("option", { value: type.value, children: type.label }, type.value)))] })] })), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "\u8AAC\u660E" }), _jsx("textarea", { value: channelForm.description || '', onChange: (e) => setChannelForm(prev => ({ ...prev, description: e.target.value })), rows: 3, className: "w-full px-3 py-2 border border-gray-300 rounded-lg", placeholder: "\u30C1\u30E3\u30F3\u30CD\u30EB\u306E\u8AAC\u660E\u3092\u5165\u529B" })] }), channelForm.provider && getProviderConfigFields(channelForm.provider, channelForm.type || '').map(field => (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: field.label }), field.type === 'checkbox' ? (_jsxs("label", { className: "flex items-center", children: [_jsx("input", { type: "checkbox", checked: channelForm.config?.[field.key] || false, onChange: (e) => setChannelForm(prev => ({
                                            ...prev,
                                            config: { ...prev.config, [field.key]: e.target.checked }
                                        })), className: "mr-2" }), field.label] })) : (_jsxs(_Fragment, { children: [_jsx("input", { type: field.type, value: channelForm.config?.[field.key] || '', onChange: (e) => setChannelForm(prev => ({
                                            ...prev,
                                            config: { ...prev.config, [field.key]: e.target.value }
                                        })), placeholder: field.placeholder, className: "w-full px-3 py-2 border border-gray-300 rounded-lg" }), field.helpText && (_jsx("p", { className: "text-xs text-gray-500 mt-1", children: field.helpText }))] }))] }, field.key)))] }) }));
    };
    // ヘルパー関数
    const getProviderIcon = (provider) => {
        switch (provider) {
            case 'slack': return '💬';
            case 'google_chat': return '💬';
            case 'email': return '📧';
            case 'websocket': return '🔌';
            case 'microsoft_teams': return '👥';
            case 'discord': return '🎮';
            case 'apns': return '📱';
            case 'fcm': return '📱';
            default: return '💬';
        }
    };
    const getProviderName = (provider) => {
        switch (provider) {
            case 'slack': return 'Slack';
            case 'google_chat': return 'Google Chat';
            case 'email': return 'Email';
            case 'websocket': return 'WebSocket';
            case 'microsoft_teams': return 'Microsoft Teams';
            case 'discord': return 'Discord';
            case 'apns': return 'APNs (iOS)';
            case 'fcm': return 'FCM (Android)';
            default: return provider;
        }
    };
    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-gray-100 text-gray-800';
            case 'error': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    return (_jsx(PageShell, { title: "\u30C1\u30E3\u30F3\u30CD\u30EB", icon: _jsx(FaHashtag, { className: "text-indigo-600" }), actions: _jsx("button", { onClick: () => setShowChannelModal(true), className: "inline-flex items-center justify-center rounded-md font-medium bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm flex items-center", children: "\u65B0\u898F\u4F5C\u6210" }), children: _jsxs("div", { className: "space-y-6", children: [error && (_jsx("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4", children: _jsxs("div", { className: "flex", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx("span", { className: "text-red-400", children: "\u26A0\uFE0F" }) }), _jsx("div", { className: "ml-3", children: _jsx("p", { className: "text-sm text-red-800", children: error }) })] }) })), success && (_jsx("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4", children: _jsxs("div", { className: "flex", children: [_jsx("div", { className: "flex-shrink-0" }), _jsx("div", { className: "ml-3", children: _jsx("p", { className: "text-sm text-green-800", children: success }) })] }) })), loading ? (_jsx("div", { className: "flex justify-center py-8", children: _jsx(Spinner, {}) })) : (renderContent()), renderChannelModal(), _jsx(ConfirmDialog, { isOpen: !!channelToDelete, onClose: () => setChannelToDelete(null), onConfirm: handleConfirmDeleteChannel, danger: true, busy: deletingChannel, title: "\u30C1\u30E3\u30F3\u30CD\u30EB\u524A\u9664\u306E\u78BA\u8A8D", message: "\u3053\u306E\u30C1\u30E3\u30F3\u30CD\u30EB\u3092\u524A\u9664\u3057\u307E\u3059\u304B\uFF1F", confirmLabel: "\u524A\u9664" })] }) }));
};
